import styled, { css } from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';

const WithDisabledState = (props: { $disabled: boolean }) =>
  props.$disabled &&
  css`
    .ant-radio {
      pointer-events: none;
      cursor: not-allowed;

      .ant-radio-inner {
        border-color: ${theme.dime.colors.border.interactiveDisabled};
        background-color: ${theme.dime.colors.surface.primaryDisabled};

        &::after {
          background-color: ${theme.dime.colors.text.disabled};
        }
      }

      &.ant-radio-checked .ant-radio-inner {
        border-color: ${theme.dime.colors.border.interactiveDisabled};
        background-color: ${theme.dime.colors.surface.primaryDisabled};

        &::after {
          background-color: ${theme.dime.colors.text.disabled};
        }
      }
    }
  `;

const WithErrorState = (props: { $hasError: boolean }) =>
  props.$hasError
    ? css`
        .ant-radio {
          .ant-radio-inner {
            border-color: ${theme.dime.colors.border.critical};
          }

          &.ant-radio-checked .ant-radio-inner {
            border-color: ${theme.dime.colors.border.critical};

            &::after {
              background-color: ${theme.dime.colors.border.critical};
            }
          }

          &:not(.ant-radio-disabled):hover {
            .ant-radio-inner {
              border-color: ${theme.dime.colors.border.interactiveHover};

              &.ant-radio-checked {
                border-color: ${theme.dime.colors.border.interactiveHover};
              }
            }
          }

          &:focus-within .ant-radio-inner {
            border-color: ${theme.dime.colors.border.critical};
            outline: none;
          }
        }
      `
    : css`
        .ant-radio {
          &:not(.ant-radio-disabled):hover {
            .ant-radio-inner {
              border-color: ${theme.dime.colors.border.interactiveHover};

              &.ant-radio-checked {
                border-color: ${theme.dime.colors.border.interactiveHover};
              }
            }
          }

          &:focus-within .ant-radio-inner {
            border-color: ${theme.dime.colors.action.primary};
            outline: none;
            background-color: #e6f0fe;
          }
        }
      `;

const StyledRadioWrapper = styled.div<{
  $disabled: boolean;
  $hasError: boolean;
}>`
  .ant-radio-wrapper {
    gap: ${theme.dime.spacing.m};
    > span:not(.ant-radio) {
      padding-left: 0;
      padding-right: 0;
    }

    .ant-radio {
      .ant-radio-inner {
        border: 1px solid ${theme.dime.colors.border.static};
        border-radius: ${theme.dime.radius.full};
        transition: border-color 0.2s ease;

        &::after {
          width: 20px;
          height: 20px;
          border-radius: ${theme.dime.radius.full};
          background-color: ${theme.dime.colors.action.primary};
          margin-top: -10px;
          margin-left: -10px;
          transition: all 0.2s ease;
          transform: scale(0.4);
        }
      }

      &.ant-radio-checked .ant-radio-inner {
        border-color: ${theme.dime.colors.border.interactiveHover};
        background-color: ${theme.dime.colors.surface.primary};
      }

      &.ant-radio-checked::after {
        opacity: 0;
      }
    }

    ${WithDisabledState}

    ${WithErrorState}
  }
`;

const StyledLabel = styled(Typography)<{
  $disabled: boolean;
  $hasError: boolean;
}>`
  color: ${(props) => {
    switch (true) {
      case props.$disabled:
        return theme.dime.colors.text.disabled;
      case props.$hasError:
        return theme.dime.colors.text.critical;
      default:
        return theme.dime.colors.text.primary;
    }
  }};
  margin: 0;
`;

const StyledHelpText = styled(Typography)<{
  $disabled: boolean;
  $hasError: boolean;
}>`
  color: ${(props) => {
    switch (true) {
      case props.$disabled:
        return theme.dime.colors.text.disabled;
      case props.$hasError:
        return theme.dime.colors.text.critical;
      default:
        return theme.dime.colors.text.secondary;
    }
  }};
  margin-top: ${theme.dime.spacing.s};
  margin-left: ${theme.dime.spacing.xl};
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  align-items: flex-start;
  justify-content: flex-start;
`;

export { StyledRadioWrapper, StyledLabel, StyledHelpText, RadioContainer };
